import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle, Phone } from "@phosphor-icons/react";
import { Header } from "@/components/site/Header";
import { ServiceArea } from "@/components/site/ServiceArea";
import { Footer } from "@/components/site/Footer";
import { Seo } from "@/components/site/Seo";
import { Reveal } from "@/components/site/Reveal";
import { getService, SERVICES } from "@/data/services";

const SITE = "https://treasurevalleyturf.com";

export default function ServicePage() {
    const { slug } = useParams();
    const svc = getService(slug);

    if (!svc) return <Navigate to="/" replace />;

    const title = `${svc.title} | Treasure Valley Turf & Greens`;
    const description = `${svc.intro.slice(0, 150)}… Free estimates across the Treasure Valley. Call (208) 605-9096.`;

    const jsonLd = [
        {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: svc.title,
            provider: { "@id": `${SITE}/#business` },
            areaServed: { "@type": "State", name: "Idaho" },
            url: `${SITE}/services/${svc.slug}`,
            description: svc.intro,
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: svc.faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
        },
    ];

    return (
        <div data-testid="service-page" className="relative min-h-screen bg-white text-brand-text">
            <Seo title={title} description={description} canonical={`${SITE}/services/${svc.slug}`} jsonLd={jsonLd} />
            <ServiceArea />
            <Header />

            <main>
                {/* Hero */}
                <section className="relative overflow-hidden pb-16 pt-44 md:pb-24 md:pt-56">
                    <div aria-hidden className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[720px] rounded-full bg-brand-accent/10 blur-[120px]" />
                    <div className="relative mx-auto max-w-7xl px-6 md:px-10">
                        <Reveal>
                            <Link to="/#services" data-testid="service-back-link" className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-muted transition-colors hover:text-brand-accent">
                                <ArrowLeft size={16} weight="bold" /> All services
                            </Link>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <h1 className="font-display max-w-4xl text-balance text-4xl font-semibold leading-[1.02] tracking-tight md:text-6xl">
                                {svc.headline.split(" ").slice(0, -2).join(" ")}{" "}
                                <span className="italic text-brand-accent">
                                    {svc.headline.split(" ").slice(-2).join(" ")}
                                </span>
                            </h1>
                        </Reveal>
                        <Reveal delay={0.16}>
                            <p className="mt-6 max-w-2xl text-base leading-relaxed text-brand-muted md:text-lg">{svc.intro}</p>
                        </Reveal>
                        <Reveal delay={0.24}>
                            <div className="mt-10 flex flex-wrap items-center gap-4">
                                <a href="/#quote" data-testid="service-cta-quote" className="group inline-flex items-center gap-2 rounded-full bg-brand-accent px-7 py-4 text-base font-bold text-white transition-all duration-300 hover:bg-brand-accentHover">
                                    Get a free quote
                                    <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
                                </a>
                                <a href="tel:+12086059096" data-testid="service-cta-call" className="inline-flex items-center gap-2 rounded-full border border-brand-text/25 px-7 py-4 text-base font-semibold text-brand-text transition-colors hover:border-brand-accent hover:text-brand-accent">
                                    <Phone size={18} weight="bold" /> (208) 605-9096
                                </a>
                            </div>
                        </Reveal>
                    </div>
                </section>

                {/* Benefits */}
                <section className="border-t border-brand-border/60 bg-brand-surface/50 py-20 md:py-28">
                    <div className="mx-auto max-w-7xl px-6 md:px-10">
                        <Reveal>
                            <h2 className="font-display text-3xl font-medium tracking-tight md:text-5xl">
                                Why homeowners choose it
                            </h2>
                        </Reveal>
                        <ul className="mt-10 grid gap-4 sm:grid-cols-2">
                            {svc.benefits.map((b, i) => (
                                <Reveal key={b} delay={i * 0.05}>
                                    <li data-testid={`service-benefit-${i}`} className="flex items-start gap-3 rounded-2xl border border-brand-border bg-white p-5 text-sm leading-relaxed text-brand-text">
                                        <CheckCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-brand-accent" />
                                        {b}
                                    </li>
                                </Reveal>
                            ))}
                        </ul>
                    </div>
                </section>

                {/* Process */}
                <section className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
                    <Reveal>
                        <h2 className="font-display text-3xl font-medium tracking-tight md:text-5xl">
                            How the install works
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid gap-4 md:grid-cols-4">
                        {svc.process.map((p, i) => (
                            <Reveal key={p.step} delay={i * 0.07}>
                                <div data-testid={`service-step-${i}`} className="h-full rounded-2xl border border-brand-border bg-white p-6 transition-colors duration-500 hover:border-brand-accent/40">
                                    <span className="font-display text-sm italic text-brand-accent">0{i + 1}</span>
                                    <h3 className="font-display mt-3 text-lg font-semibold">{p.step}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-brand-muted">{p.detail}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </section>

                {/* FAQ */}
                <section className="border-t border-brand-border/60 bg-brand-surface/50 py-20 md:py-28">
                    <div className="mx-auto max-w-4xl px-6 md:px-10">
                        <Reveal>
                            <h2 className="font-display text-3xl font-medium tracking-tight md:text-5xl">Common questions</h2>
                        </Reveal>
                        <div className="mt-10 space-y-4">
                            {svc.faq.map((f, i) => (
                                <Reveal key={f.q} delay={i * 0.06}>
                                    <div data-testid={`service-faq-${i}`} className="rounded-2xl border border-brand-border bg-white p-6">
                                        <h3 className="font-display text-lg font-semibold">{f.q}</h3>
                                        <p className="mt-2 text-sm leading-relaxed text-brand-muted md:text-base">{f.a}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                        <Reveal delay={0.2}>
                            <div className="mt-12 flex flex-col items-center gap-4 rounded-3xl border border-brand-accent/30 bg-brand-accent/5 p-8 text-center">
                                <p className="font-display text-2xl font-medium">Ready for a flat, no-surprise quote?</p>
                                <a href="/#quote" data-testid="service-bottom-cta" className="group inline-flex items-center gap-2 rounded-full bg-brand-accent px-7 py-4 text-base font-bold text-white transition-all duration-300 hover:bg-brand-accentHover">
                                    Get your free quote
                                    <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
                                </a>
                            </div>
                        </Reveal>
                    </div>
                </section>

                {/* Other services */}
                <section className="mx-auto max-w-7xl px-6 py-20 md:px-10">
                    <Reveal>
                        <h2 className="font-display text-2xl font-medium tracking-tight md:text-4xl">Explore our other services</h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="mt-8 flex flex-wrap gap-3">
                            {SERVICES.filter((s) => s.slug !== svc.slug).map((s) => (
                                <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`service-link-${s.slug}`} className="rounded-full border border-brand-border bg-white px-5 py-2.5 text-sm font-semibold text-brand-text transition-colors hover:border-brand-accent hover:text-brand-accent">
                                    {s.name}
                                </Link>
                            ))}
                        </div>
                    </Reveal>
                </section>
            </main>

            <Footer />
        </div>
    );
}
